import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "@/lib/api";

export type Features = { shop: boolean; guides: boolean };

const DEFAULT_FEATURES: Features = { shop: false, guides: false };

type FeaturesCtx = { features: Features; loading: boolean };

const FeaturesContext = createContext<FeaturesCtx>({
  features: DEFAULT_FEATURES,
  loading: true,
});

/**
 * Loads public feature flags (which site sections are live) once at startup
 * from /api/site/features. Defaults to everything-off until loaded, so a
 * disabled section never flashes visible.
 */
export function FeaturesProvider({ children }: { children: ReactNode }) {
  const [features, setFeatures] = useState<Features>(DEFAULT_FEATURES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ features: Features }>("/api/site/features")
      .then((r) => setFeatures({ ...DEFAULT_FEATURES, ...r.features }))
      .catch(() => setFeatures(DEFAULT_FEATURES))
      .finally(() => setLoading(false));
  }, []);

  return (
    <FeaturesContext.Provider value={{ features, loading }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures(): FeaturesCtx {
  return useContext(FeaturesContext);
}
